import { I18nPluralPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    ElementRef,
    inject,
    input,
    linkedSignal,
    NgZone,
    signal,
    viewChild,
    viewChildren,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { CurrentUser } from '@interacta-shared/data-access-auth';
import { GoogleAnalyticsService } from '@interacta-shared/data-access-common';
import {
    ApplyPipe,
    ArrayPipe,
    IconButtonComponent,
    ImageComponent,
    LiveStatusComponent,
    LoaderComponent,
    LoadMoreComponent,
    ScrollTrackerDirective,
    ScrollTrackerEvent,
} from '@interacta-shared/ui';
import { FeedbackChip } from '@interacta-shared/ui-ai';
import { AiLoaderComponent } from '@interacta-shared/ui-common';
import { IconComponent } from '@interacta-shared/ui-icons';
import {
    getAbbreviation,
    getNextPageToken,
    Index,
    lastLoading,
    uuid,
} from '@interacta-shared/util';
import {
    focusFirstNewChild,
    getRemainingDaysInMonth,
} from '@interacta-shared/util-common';
import { PostType } from '@interacta-shared/util-post-lite';
import { AICreditSelectors } from '@interacta/data-access-ai';
import { selectCommunityTree } from '@interacta/data-access-community-tree';
import { ICommunity } from '@interacta/util-community';
import { IAttachment } from '@interacta/util-post';
import { AIConvoThinkingLineComponent } from '@modules/ai-convo/components/ai-convo-thinking-line/ai-convo-thinking-line.component';
import { AIConvoThinkingStepsComponent } from '@modules/ai-convo/components/ai-convo-thinking-steps/ai-convo-thinking-steps.component';
import { AI_CONVO_QUESTION_MAX_LENGTH } from '@modules/ai-convo/constants';
import { getAgentColorClass } from '@modules/ai-convo/helpers/ai-convo-panel.utils';
import {
    AI_CONVO_FILTER_CHIP_CONTEXT_TYPES,
    AIConvoContextType,
} from '@modules/ai-convo/models/ai-convo-context.model';
import { AISharedAttachmentChipComponent } from '@modules/ai-shared/components/ai-shared-attachment-chip/ai-shared-attachment-chip.component';
import { AISharedAttachmentPickerComponent } from '@modules/ai-shared/components/ai-shared-attachment-picker/ai-shared-attachment-picker.component';
import { AISharedInputComponent } from '@modules/ai-shared/components/ai-shared-input/ai-shared-input.component';
import { AISharedModelMessageBalloonComponent } from '@modules/ai-shared/components/ai-shared-model-message-balloon/ai-shared-model-message-balloon.component';
import { AISharedReferenceChipComponent } from '@modules/ai-shared/components/ai-shared-reference-chip/ai-shared-reference-chip.component';
import { AISharedUserMessageBalloonComponent } from '@modules/ai-shared/components/ai-shared-user-message-balloon/ai-shared-user-message-balloon.component';
import { QuickActionChipComponent } from '@modules/ai-shared/components/quick-action-chip/quick-action-chip.component';
import { AI_SHARED_SMART_ACTIONS } from '@modules/ai-shared/constants/smart-actions.constants';
import {
    AISharedGenericReference,
    AISharedModelMessage,
    AISharedQuickActionChipMode,
    AISharedReferenceType,
    AISharedRole,
    AISharedSmartAction,
    AISharedSmartActionId,
} from '@modules/ai-shared/models/ai-shared.model';
import { AISharedReferenceViewService } from '@modules/ai-shared/services/ai-shared-reference-view.service';
import * as CommunitySelectors from '@modules/communities/store/community/community.selectors';
import * as PostSelectors from '@modules/communities/store/post/post.selectors';
import { CommunitiesMultipleSelectDialogComponent } from '@modules/shared-v2/components/communities-multiple-select-dialog/communities-multiple-select-dialog.component';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AIConvoActions } from '../../store/ai-convo.actions';
import {
    selectActiveAgentForChat,
    selectActiveAIConvoChat,
    selectAICommunityFilter,
    selectAICommunityFilterMode,
    selectAIConvoContext,
    selectAIConvoPendingAttachment,
    selectAIConvoPendingAttachmentSource,
    selectAIConvoPendingReference,
} from '../../store/ai-convo.selectors';

/** What a question is scoped to when it is not the pending attachment or source. */
type AIConvoQuestionScope = {
    attachment?: IAttachment | null;
    reference: AISharedGenericReference | null;
    smartActionId: AISharedSmartActionId | null;
};

@Component({
    selector: 'interacta-ai-convo-chat-area',
    templateUrl: './ai-convo-chat-area.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        AiLoaderComponent,
        AISharedAttachmentChipComponent,
        AISharedAttachmentPickerComponent,
        AISharedInputComponent,
        AISharedModelMessageBalloonComponent,
        AISharedReferenceChipComponent,
        AISharedUserMessageBalloonComponent,
        AIConvoThinkingLineComponent,
        AIConvoThinkingStepsComponent,
        ApplyPipe,
        ArrayPipe,
        CommunitiesMultipleSelectDialogComponent,
        IconButtonComponent,
        IconComponent,
        ImageComponent,
        I18nPluralPipe,
        LiveStatusComponent,
        LoaderComponent,
        LoadMoreComponent,
        NgClass,
        NgTemplateOutlet,
        QuickActionChipComponent,
        ScrollTrackerDirective,
        TranslatePipe,
    ],
})
export class AIConvoChatAreaComponent implements AfterViewInit {
    private readonly store = inject(Store);
    private readonly translateService = inject(TranslateService);
    private readonly gaService = inject(GoogleAnalyticsService);
    private readonly zone = inject(NgZone);
    private readonly referenceViewService = inject(
        AISharedReferenceViewService,
    );

    private focusObserver: MutationObserver | null = null;
    private scrollAnchorObserver: MutationObserver | null = null;
    private anchorTopObserver: MutationObserver | null = null;
    // One-shot: on the next rendered batch after a send or a chat opening,
    // evaluate whether to anchor the user's latest message to the top. Disarmed
    // right after so the incoming answer never re-scrolls the view. The reason
    // differs only when everything fits: a send stays put, a restore goes to the
    // bottom (the whole chat is visible anyway).
    private pendingAnchor: 'send' | 'restore' | null = null;
    // True while the last turn is top-anchored: the bottom spacer is kept sized
    // to exactly fill the viewport below the user message (no more, no less), so
    // the answer growing in fills the reserved space and the view can never
    // scroll past the anchored position into empty room.
    private maintainSpacer = false;
    private anchorRafScheduled = false;
    // scrollTop read in the capture phase of a click, before the clicked control
    // changes the layout. A collapse shrinks scrollHeight and the browser clamps
    // scrollTop before the spacer is resized, so the live value is already wrong.
    private lastClick: {
        target: EventTarget | null;
        scrollTop: number;
        at: number;
    } | null = null;
    private autoLoadScheduled = false;

    constructor() {
        const destroyRef = inject(DestroyRef);
        destroyRef.onDestroy(() => {
            this.focusObserver?.disconnect();
            this.scrollAnchorObserver?.disconnect();
            this.anchorTopObserver?.disconnect();
        });

        // Opening (or switching) a chat re-evaluates anchoring from scratch.
        toObservable(this.activeChatId)
            .pipe(takeUntilDestroyed())
            .subscribe((id) => {
                if (id != null) {
                    this.pendingAnchor = 'restore';
                    this.maintainSpacer = false;
                }
            });

        // Auto-fill the viewport with older pages while the list still fits (the
        // "Load more" button is visible without scrolling), mirroring
        // post-table-v2. Observe only the load-more container: it appears
        // (childList) and swaps button↔loader on each fetch (class attribute) —
        // so the check re-runs per page without reacting to message streaming.
        const autoLoadObserver = new MutationObserver(() =>
            this.scheduleAutoLoad(),
        );
        toObservable(this.loadMoreContainer)
            .pipe(takeUntilDestroyed())
            .subscribe((container) =>
                this.zone.runOutsideAngular(() => {
                    autoLoadObserver.disconnect();
                    if (container) {
                        autoLoadObserver.observe(container.nativeElement, {
                            attributeFilter: ['class'],
                            attributes: true,
                            childList: true,
                            subtree: true,
                        });
                        this.scheduleAutoLoad();
                    }
                }),
            );
        destroyRef.onDestroy(() => autoLoadObserver.disconnect());
    }

    readonly currentUser = input<CurrentUser | null>();
    readonly currentThemeMode = input<string>();

    readonly inputRef = viewChild<AISharedInputComponent>('input');
    readonly scrollDiv = viewChild<ElementRef<HTMLDivElement>>('scrollDiv');
    private readonly bottomSpacer =
        viewChild<ElementRef<HTMLElement>>('bottomSpacer');
    private readonly loadMoreContainer =
        viewChild<ElementRef<HTMLElement>>('loadMoreContainer');
    readonly modelMessageBalloons = viewChildren(
        AISharedModelMessageBalloonComponent,
    );

    private readonly communityList = this.store.selectSignal(
        CommunitySelectors.selectCommunityList,
    );
    private readonly communityTree =
        this.store.selectSignal(selectCommunityTree);
    readonly communityWorkspaces = computed(
        () => this.communityTree()?.workspaceList ?? [],
    );

    readonly communityFilter = this.store.selectSignal(selectAICommunityFilter);
    readonly context = this.store.selectSignal(selectAIConvoContext);
    readonly pendingAttachment = this.store.selectSignal(
        selectAIConvoPendingAttachment,
    );
    readonly pendingReference = this.store.selectSignal(
        selectAIConvoPendingReference,
    );
    private readonly pendingAttachmentSource = this.store.selectSignal(
        selectAIConvoPendingAttachmentSource,
    );
    readonly activeChat = this.store.selectSignal(selectActiveAIConvoChat);
    private readonly activeChatId = computed(
        () => this.activeChat()?.id ?? null,
    );

    private readonly communityFilterMode = this.store.selectSignal(
        selectAICommunityFilterMode,
    );

    readonly currentUserFirstName = computed(
        () => this.currentUser()?.firstName,
    );
    readonly messages = computed(() => this.activeChat()?.messages ?? []);
    readonly thinkingSteps = computed(
        () => this.activeChat()?.thinkingSteps ?? [],
    );
    readonly hasMessages = computed(() => this.messages().length > 0);
    private readonly historyEndId = linkedSignal<
        { chatId: Index | null; lastId: Index | null },
        Index | null
    >({
        source: () => ({
            chatId: this.activeChatId(),
            lastId: this.messages().at(-1)?.id ?? null,
        }),
        computation: ({ chatId, lastId }, previous) =>
            previous?.source.chatId === chatId && previous.value !== null
                ? previous.value
                : lastId,
    });
    private readonly revealingIds = linkedSignal<
        Index | null,
        ReadonlySet<Index>
    >({
        source: this.activeChatId,
        computation: () => new Set(),
    });
    readonly messageRows = computed(() => {
        const messages = this.messages();
        const historyEndId = this.historyEndId();
        const revealingIds = this.revealingIds();
        const liveStart =
            historyEndId === null
                ? 0
                : messages.findIndex(({ id }) => id === historyEndId) + 1;
        const revealing = messages.findIndex(
            (message, index) =>
                index >= liveStart &&
                (revealingIds.has(message.id) ||
                    (message.role === AISharedRole.MODEL &&
                        !message.alreadyAnimated)),
        );
        const revealingStart = revealing === -1 ? messages.length : revealing;
        const rows = messages.map((entry, index) => ({
            entry,
            index,
            last: index === messages.length - 1,
        }));
        return {
            history: rows.slice(0, liveStart),
            live: rows.slice(liveStart, revealingStart),
            revealing: rows.slice(revealingStart),
        };
    });
    readonly isChatFetching = computed(
        () => this.activeChat()?.isFetching ?? false,
    );
    readonly messagesNextPageToken = computed(
        () => this.activeChat()?.messagesNextPageToken ?? lastLoading(),
    );
    readonly messagesFetching = computed(
        () => this.activeChat()?.messagesFetching ?? false,
    );
    readonly canAutoLoadOlder = computed(
        () =>
            this.hasMessages() &&
            !this.isChatFetching() &&
            !this.messagesFetching() &&
            this.messagesNextPageToken().tag === 'regularLoading',
    );

    readonly tokenLimitExceeded = this.store.selectSignal(
        AICreditSelectors.selectCreditLimitExceeded,
    );
    readonly isBlockedByTokenLimit = computed(
        () => !this.activeAgent() && this.tokenLimitExceeded(),
    );
    readonly remainingDays = getRemainingDaysInMonth();

    readonly communityFilterName = computed(() => {
        const filterIds = this.communityFilter();
        const context = this.context();
        const idToFind =
            filterIds?.[0] ??
            (context?.type === AIConvoContextType.COMMUNITY
                ? context.communityId
                : null);
        if (idToFind === null) return null;
        const found = this.communityList().find((c) => c.id === idToFind);
        return found ? found.name.toString() : null;
    });

    readonly activeAgent = this.store.selectSignal(selectActiveAgentForChat);

    readonly activeAgentAbbreviation = computed(() => {
        const a = this.activeAgent();
        return a ? getAbbreviation(a.name).toUpperCase() : '';
    });

    readonly activeAgentColorClass = computed(() =>
        getAgentColorClass(this.activeAgent()?.color),
    );

    private readonly detailPost = this.store.selectSignal(
        PostSelectors.selectDetail,
    );

    /**
     * The picker only makes sense on a post, and only where the AI can actually
     * read it: custom posts and events. The id check guards against a stale detail
     * still in the store while another post is opening.
     */
    readonly postContextId = computed(() => {
        const context = this.context();
        if (context?.type !== AIConvoContextType.POST) return null;

        const post = this.detailPost();
        return post?.id === context.postId &&
            (post.type === PostType.CUSTOM || post.type === PostType.EVENT)
            ? context.postId
            : null;
    });

    /** Ready-made questions about the open post, offered while the chat is still empty. */
    readonly postSuggestions = computed(() =>
        this.postContextId() !== null &&
        !this.hasMessages() &&
        !this.messagesFetching() &&
        this.activeAgent() === null &&
        this.pendingAttachment() === null &&
        this.pendingReference() === null
            ? AI_SHARED_SMART_ACTIONS[AISharedReferenceType.POST]
            : [],
    );

    readonly isChipVisible = computed(() => {
        const context = this.context();
        return (
            context !== null &&
            AI_CONVO_FILTER_CHIP_CONTEXT_TYPES.has(context.type) &&
            this.activeAgent() === null &&
            this.pendingAttachment() === null &&
            this.pendingReference() === null
        );
    });

    readonly chipMode = computed((): AISharedQuickActionChipMode => {
        const lockedMode = this.communityFilterMode();
        if (lockedMode !== null) return lockedMode;
        return this.context()?.type === AIConvoContextType.COMMUNITY
            ? 'single'
            : 'multiple';
    });

    readonly chipCount = computed(() => {
        if (this.chipMode() !== 'multiple') return null;
        return this.communityFilter()?.length ?? null;
    });

    readonly chipLabel = computed(() => {
        const filterIds = this.communityFilter();
        if (filterIds !== null) {
            if (this.chipMode() === 'multiple') {
                return this.translateService.instant(
                    'AI.SIDE_PANEL.CHIP_COMMUNITY_LABEL',
                );
            }
            return this.translateService.instant(
                'AI.SIDE_PANEL.CHIP_ONLY_IN_COMMUNITY',
                { communityName: this.communityFilterName() },
            );
        }
        if (this.context()?.type === AIConvoContextType.COMMUNITY) {
            return this.translateService.instant(
                'AI.SIDE_PANEL.CHIP_SEARCH_IN_COMMUNITY',
                { communityName: this.communityFilterName() },
            );
        }
        return this.translateService.instant(
            'AI.SIDE_PANEL.CHIP_SELECT_COMMUNITY',
        );
    });

    readonly AISharedRole = AISharedRole;
    readonly getAgentColorClass = getAgentColorClass;

    /** Null for a chat with an agent: it keeps the synchronous route, which has no such limit. */
    readonly questionLimit = computed(() =>
        this.activeAgent() ? null : AI_CONVO_QUESTION_MAX_LENGTH,
    );

    readonly questionControl = new FormControl<string>('', {
        nonNullable: true,
    });
    readonly communityIdsControl = new FormControl<number[]>([], {
        nonNullable: true,
    });

    readonly showCommunityPicker = signal(false);

    ngAfterViewInit(): void {
        this.focusInput();
        this.observeAnchorTop();
    }

    // Fires once on the first rendered batch after the anchor is armed (a send or
    // a chat opening): pins the latest user message to the top, then disarms so
    // the AI answer rendering afterwards does not re-scroll the view. The
    // older-page load on user scroll never arms it, preserving reading position.
    private observeAnchorTop(): void {
        const container = this.scrollDiv()?.nativeElement;
        if (!container) return;

        this.anchorTopObserver?.disconnect();
        // Runs outside Angular: the callback only touches scroll, so it must not
        // trigger change detection on every rendered message.
        this.zone.runOutsideAngular(() => {
            container.addEventListener(
                'click',
                (event) => {
                    this.lastClick = {
                        target: event.target,
                        scrollTop: container.scrollTop,
                        at: performance.now(),
                    };
                },
                { capture: true },
            );
            const observer = new MutationObserver((mutations) => {
                // The clicked panel flipped `hidden`: fixed synchronously, in the
                // same task as the DOM update, so no frame is painted with the
                // clamped scroll position.
                const toggledScrollTop = this.takeToggledScrollTop(mutations);
                if (toggledScrollTop !== null && !this.pendingAnchor) {
                    this.keepScrollAfterToggle(toggledScrollTop);
                }
                if (
                    (!this.pendingAnchor && !this.maintainSpacer) ||
                    !mutations.some((m) => m.type === 'childList') ||
                    this.anchorRafScheduled
                )
                    return;
                // Coalesce bursts (async pages, the answer's nested render, a
                // feedback panel opening) into one deferred pass, so freshly
                // added/removed nodes are laid out before we measure.
                this.anchorRafScheduled = true;
                requestAnimationFrame(() => {
                    this.anchorRafScheduled = false;
                    if (this.pendingAnchor) {
                        const reason = this.pendingAnchor;
                        // On restore the messages load async, so the anchor stays
                        // armed until it finds the user message (an early mutation
                        // may be just the loading spinner).
                        if (this.anchorLastUserMessageTop(reason)) {
                            this.pendingAnchor = null;
                        }
                    } else if (this.maintainSpacer) {
                        // The turn height changed (answer arrived, references or
                        // the feedback detail toggled): resize the spacer to keep
                        // the invariant, without scrolling.
                        this.adjustBottomSpacer();
                    }
                });
            });
            // subtree: nested changes inside a balloon (feedback detail, expanded
            // references) must also trigger a spacer recompute.
            // `hidden` flips on collapsible panels (reasoning steps) change the
            // height without adding or removing nodes.
            observer.observe(container, {
                attributeFilter: ['hidden'],
                attributes: true,
                childList: true,
                subtree: true,
            });
            this.anchorTopObserver = observer;
        });
    }

    // Sizes the bottom spacer so the content below the last user message exactly
    // fills one viewport (collapsing it when the answer already overflows).
    // Returns the geometry needed to anchor, or a status when it can't/needn't:
    //  - 'no-user': the user message isn't rendered yet (caller should retry);
    //  - 'fits': the whole chat fits, no reserved space needed;
    //  - a number: the scrollTop that puts the user message at the top.
    private updateBottomSpacer(): 'no-user' | 'fits' | number {
        const container = this.scrollDiv()?.nativeElement;
        if (!container) return 'no-user';

        const userMessages = container.querySelectorAll<HTMLElement>(
            '.ai-convo-message-user',
        );
        const lastUserMessage = userMessages[userMessages.length - 1];
        if (!lastUserMessage) return 'no-user';

        const spacer = this.bottomSpacer()?.nativeElement;
        // Measure the real content with the spacer collapsed first.
        if (spacer) spacer.style.height = '0px';

        if (container.scrollHeight <= container.clientHeight) return 'fits';

        const paddingTop =
            parseFloat(getComputedStyle(container).paddingTop) || 0;
        const userMessageTop =
            lastUserMessage.getBoundingClientRect().top -
            container.getBoundingClientRect().top +
            container.scrollTop;
        const contentBelow = container.scrollHeight - userMessageTop;
        const missing = container.clientHeight - paddingTop - contentBelow;

        if (spacer) {
            spacer.style.height = missing > 0 ? `${missing}px` : '0px';
        }

        return userMessageTop - paddingTop;
    }

    // Returns false when the user message isn't rendered yet, so the caller can
    // keep the anchor armed and retry on the next mutation (restore loads async).
    private anchorLastUserMessageTop(reason: 'send' | 'restore'): boolean {
        const result = this.updateBottomSpacer();
        if (result === 'no-user') return false;

        const container = this.scrollDiv()?.nativeElement;
        if (!container) return true;

        if (result === 'fits') {
            this.maintainSpacer = false;
            if (reason === 'restore') {
                // The whole chat is visible anyway — drop to the bottom.
                container.scrollTo({
                    top: container.scrollHeight,
                    behavior: 'auto',
                });
                return true;
            }
            // Send that still fits: if the answer hasn't arrived yet, stay armed
            // — the incoming answer may overflow and then must anchor. Once it's
            // here and it still fits, it's a short exchange: stay put, done.
            return !this.isChatFetching();
        }

        // Overflow: keep the spacer sized to the viewport from now on, so the
        // incoming answer fills it and the view can't scroll into empty room.
        this.maintainSpacer = true;
        container.scrollTo({
            top: result,
            behavior: reason === 'restore' ? 'auto' : 'smooth',
        });
        return true;
    }

    // Recomputes the spacer as the anchored turn's height changes (answer
    // arriving, references expanding), never scrolling — so the reserved empty
    // space always ends exactly at the viewport edge.
    private adjustBottomSpacer(): void {
        // Collapsing the spacer to measure momentarily shrinks scrollHeight, which
        // clamps scrollTop; since this path never re-scrolls, capture and restore
        // it so an anchored message doesn't slide down (e.g. references collapse).
        const container = this.scrollDiv()?.nativeElement;
        const scrollTop = container?.scrollTop ?? 0;
        if (this.updateBottomSpacer() === 'fits') {
            this.maintainSpacer = false;
        }
        if (container) container.scrollTop = scrollTop;
    }

    // The scrollTop recorded by the last click, when that click toggled a panel:
    // a `hidden` flip on an element whose parent holds the clicked control. The
    // same flip on a panel inserted by an unrelated render (the initial `hidden`
    // of a new answer's steps) is not a toggle.
    private takeToggledScrollTop(mutations: MutationRecord[]): number | null {
        const click = this.lastClick;
        if (!click || performance.now() - click.at >= 500) return null;
        const toggled = mutations.some(
            (m) =>
                m.type === 'attributes' &&
                click.target instanceof Node &&
                m.target.parentElement?.contains(click.target),
        );
        if (!toggled) return null;
        this.lastClick = null;
        return click.scrollTop;
    }

    // A clicked panel flipped `hidden`. A collapse can leave less content than
    // the scroll position needs (scrollHeight never drops below the viewport),
    // so the spacer is sized to the room still missing: the browser then has
    // nothing to clamp and the clicked control stays put.
    private keepScrollAfterToggle(scrollTop: number): void {
        const container = this.scrollDiv()?.nativeElement;
        if (!container) return;
        const spacer = this.bottomSpacer()?.nativeElement;
        if (this.maintainSpacer) this.adjustBottomSpacer();
        else if (spacer) spacer.style.height = '0px';
        if (spacer) {
            const contentEnd =
                spacer.getBoundingClientRect().top -
                container.getBoundingClientRect().top +
                container.scrollTop;
            const needed =
                scrollTop +
                container.clientHeight -
                contentEnd -
                (parseFloat(getComputedStyle(container).paddingBottom) || 0);
            const current = parseFloat(spacer.style.height) || 0;
            spacer.style.height = `${Math.max(current, needed)}px`;
        }
        container.scrollTop = scrollTop;
    }

    focusInput(): void {
        requestAnimationFrame(() =>
            this.inputRef()?.textArea()?.nativeElement?.focus(),
        );
    }

    /** Without `scope` the question carries the pending attachment or source. */
    sendMessage(message?: string, scope?: AIConvoQuestionScope): void {
        if (!message || this.isChatFetching()) return;

        const limit = this.questionLimit();
        if (!scope && limit !== null && message.length > limit) return;

        const activeChat = this.activeChat();
        if (!activeChat) return;

        this.modelMessageBalloons().forEach((balloon) =>
            balloon.completeAnimation(),
        );

        const attachment = scope
            ? (scope.attachment ?? null)
            : this.pendingAttachment();
        const reference = scope ? scope.reference : this.pendingReference();
        const smartActionId = scope?.smartActionId ?? null;

        this.gaService.sendCustomEvent('isp_ai_conversational_message_sent', {
            type: !attachment
                ? 'plain'
                : this.pendingAttachmentSource() === 'action'
                  ? 'attachment_from_action'
                  : 'attachment_from_picker',
        });

        this.store.dispatch(
            AIConvoActions.fetchResponseFromAI({
                chatId: activeChat.id,
                communityFilter: this.communityFilter(),
                context: this.context(),
                sessionId: activeChat.sessionId,
                attachment,
                reference,
                smartActionId,
                fromInput: !scope,
                message,
                messageId: uuid(),
                timestamp: new Date(),
            }),
        );

        // Arm the one-shot anchor: when the new user message + AI placeholder
        // render, if they overflow the viewport the observer scrolls the message
        // to the top with room below for the incoming answer.
        this.pendingAnchor = 'send';
        if (!scope) this.questionControl.reset();
    }

    selectAttachment(attachment: IAttachment): void {
        this.store.dispatch(
            AIConvoActions.setPendingAttachment({
                pending: { attachment, source: 'picker' },
            }),
        );
        this.focusInput();
    }

    openPendingAttachment(attachment: IAttachment): void {
        this.referenceViewService.openAttachment(attachment);
    }

    askReference(reference: AISharedGenericReference): void {
        this.store.dispatch(AIConvoActions.setPendingReference({ reference }));
        this.focusInput();
    }

    openPendingReference(reference: AISharedGenericReference): void {
        this.referenceViewService.open(reference, 'list');
    }

    clearPendingReference(): void {
        this.store.dispatch(
            AIConvoActions.setPendingReference({ reference: null }),
        );
    }

    sendSmartAction({
        reference,
        action,
    }: {
        reference: AISharedGenericReference;
        action: AISharedSmartAction;
    }): void {
        this.sendMessage(this.translateService.instant(action.message), {
            reference,
            smartActionId: action.id,
        });
        this.focusInput();
    }

    /** Re-asks the question before `answerIndex`, with the same attachment and source. */
    retryMessage(answerIndex: number): void {
        const question = this.messages()[answerIndex - 1];
        if (question?.role !== AISharedRole.USER) return;

        this.sendMessage(question.content, {
            attachment: question.attachment?.attachmentData ?? null,
            reference: question.reference ?? null,
            smartActionId: question.smartActionId,
        });
    }

    sendSuggestion(suggestion: AISharedSmartAction): void {
        this.sendMessage(this.translateService.instant(suggestion.message), {
            reference: null,
            smartActionId: suggestion.id,
        });
    }

    clearPendingAttachment(): void {
        this.store.dispatch(
            AIConvoActions.setPendingAttachment({ pending: null }),
        );
    }

    onChipClick(): void {
        if (this.chipMode() === 'multiple') {
            this.communityIdsControl.setValue(this.communityFilter() ?? []);
            this.showCommunityPicker.set(true);
            return;
        }
        if (this.communityFilter() !== null) {
            this.store.dispatch(
                AIConvoActions.setCommunityFilter({
                    communityIds: null,
                    mode: null,
                }),
            );
            return;
        }
        const context = this.context();
        if (context?.type === AIConvoContextType.COMMUNITY) {
            this.store.dispatch(
                AIConvoActions.setCommunityFilter({
                    communityIds: [context.communityId],
                    mode: 'single',
                }),
            );
        }
    }

    onCommunitiesConfirmed(communityIds: ICommunity['id'][]): void {
        this.store.dispatch(
            AIConvoActions.setCommunityFilter({
                communityIds: communityIds.length ? communityIds : null,
                mode: communityIds.length ? 'multiple' : null,
            }),
        );
    }

    onCommunityPickerConfirm(): void {
        this.onCommunitiesConfirmed(this.communityIdsControl.value);
        this.onCloseDialog();
    }

    onCloseDialog(): void {
        this.showCommunityPicker.set(false);
    }

    sendFeedbackLike(
        message: AISharedModelMessage,
        like: boolean | undefined,
    ): void {
        const chatId = this.activeChat()?.id;
        if (!chatId) return;

        this.store.dispatch(
            AIConvoActions.sendFeedback({
                chatId,
                messageId: message.id,
                like,
            }),
        );
    }

    sendFeedbackDetail(
        message: AISharedModelMessage,
        $event: {
            chips: FeedbackChip[];
            moreDetails: string | null;
        },
    ): void {
        const chatId = this.activeChat()?.id;
        if (!chatId) return;

        if (message.feedbackLike === undefined) return;

        this.store.dispatch(
            AIConvoActions.sendFeedbackDetail({
                chatId,
                messageId: message.id,
                like: message.feedbackLike,
                feedbacks: $event.chips?.map((c) => c.id),
                text: $event.moreDetails ?? undefined,
            }),
        );
    }

    onMessageAnimated(messageId: Index): void {
        const chatId = this.activeChat()?.id;
        if (!chatId) return;

        this.revealingIds.update((ids) => new Set(ids).add(messageId));

        this.store.dispatch(
            AIConvoActions.setMessageAlreadyAnimated({
                chatId,
                messageId,
            }),
        );
    }

    onMessageRevealed(messageId: Index): void {
        this.revealingIds.update((ids) => {
            if (!ids.has(messageId)) return ids;
            const next = new Set(ids);
            next.delete(messageId);
            return next;
        });
    }

    onScroll(event: ScrollTrackerEvent): void {
        // Oldest messages are at the top; load the next (older) page when the
        // user scrolls near the top of the list.
        if (
            event.offsetReached &&
            !this.messagesFetching() &&
            this.messagesNextPageToken().tag === 'regularLoading'
        ) {
            this.loadOlderMessages();
        }
    }

    loadOlderMessages(): void {
        const chat = this.activeChat();
        const container = this.scrollDiv()?.nativeElement;
        if (!chat?.historyId || this.messagesFetching() || !container) return;

        // The user is deliberately going back through history: cancel any pending
        // anchor, stop maintaining the spacer, and preserve the reading position.
        this.pendingAnchor = null;
        this.maintainSpacer = false;

        // Preserve the visual scroll position when the older page prepends at
        // the top: focus alone uses `preventScroll`, so without compensating for
        // the added content height the viewport would jump to the oldest message.
        this.anchorScrollOnPrepend(container);

        // Move focus (and scroll) to the newest message of the page about to
        // load, once it renders — keeps the user at the boundary with what they
        // were reading instead of jumping to the oldest or to the bottom.
        this.focusObserver = focusFirstNewChild({
            container,
            currentChildCount: this.messages().length,
            pendingObserver: this.focusObserver,
            childSelector: '.ai-convo-message',
            // The wrapper itself is the focus target (tabindex="-1"), not a
            // focusable descendant — empty string selects the child element.
            focusableInChildSelectors: '',
            upDirection: true,
        });

        const pageToken =
            getNextPageToken(this.messagesNextPageToken()) ?? undefined;

        this.store.dispatch(
            AIConvoActions.fetchSessionMessages({
                chatId: chat.id,
                historyId: chat.historyId,
                pageToken,
            }),
        );
    }

    // Keeps the content the user was reading in place when an older page is
    // prepended: records the scroll metrics before the fetch and, once the new
    // messages render, shifts scrollTop by the height they added.
    private anchorScrollOnPrepend(container: HTMLDivElement): void {
        this.scrollAnchorObserver?.disconnect();
        const prevScrollHeight = container.scrollHeight;
        const prevScrollTop = container.scrollTop;

        // Runs outside Angular: the callback only touches scroll, so it must not
        // trigger change detection when the older page renders.
        this.zone.runOutsideAngular(() => {
            const observer = new MutationObserver((mutations) => {
                if (
                    !mutations.some(
                        (m) =>
                            m.type === 'childList' && m.addedNodes.length > 0,
                    )
                )
                    return;
                container.scrollTop =
                    prevScrollTop + (container.scrollHeight - prevScrollHeight);
                observer.disconnect();
            });
            observer.observe(container, { childList: true });
            this.scrollAnchorObserver = observer;
        });
    }

    private scheduleAutoLoad(): void {
        if (this.autoLoadScheduled) return;
        this.autoLoadScheduled = true;
        requestAnimationFrame(() => {
            this.autoLoadScheduled = false;
            this.checkAutoLoad();
        });
    }

    // Fills the viewport with older pages while the list still fits (no scroll
    // yet); unlike `loadOlderMessages` it does not move focus, since keyboard
    // users reach older pages through the visible "Load more" button.
    private checkAutoLoad(): void {
        const chat = this.activeChat();
        if (!chat?.historyId || !this.canAutoLoadOlder()) return;
        const historyId = chat.historyId;

        const el = this.scrollDiv()?.nativeElement;
        if (!el || el.scrollHeight - el.clientHeight > 1) return;

        const pageToken =
            getNextPageToken(this.messagesNextPageToken()) ?? undefined;

        this.zone.run(() =>
            this.store.dispatch(
                AIConvoActions.fetchSessionMessages({
                    chatId: chat.id,
                    historyId,
                    pageToken,
                }),
            ),
        );
    }
}
